import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDomains } from "@/hooks/useDomains";
import { useOrgRole } from "@/hooks/useOrgRole";
import { usePages } from "@/hooks/usePages";
import { extractDomain, cn } from "@/lib/utils";
import { useStore } from "@/store";
import { ChevronDown, Globe, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function WorkspaceSwitcher() {
  const { domains, currentDomain, setCurrentDomainId } = useDomains();
  const { setCurrentPageId } = usePages();
  const resetHeaderTools = useStore((s) => s.resetHeaderTools);
  const { isAdmin } = useOrgRole();
  const navigate = useNavigate();

  const handleSelect = (domainId: string) => {
    if (domainId === currentDomain?.id) return;
    setCurrentDomainId(domainId);
  };

  const handleAddNewWebsite = async () => {
    // Clear the active workspace selection before navigating to the Setup
    // Wizard, so it mounts with no stale domain pre-filled from this view.
    await setCurrentDomainId(null);
    await setCurrentPageId(null);
    resetHeaderTools();
    navigate("/setup-wizard?reconnect=true");
  };

  const triggerLabel = currentDomain
    ? extractDomain(currentDomain.url)
    : "Select a website";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-10 min-w-0 items-center gap-2 rounded-md border border-input px-3 text-sm shrink-0 hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="max-w-[160px] truncate font-medium">
            {triggerLabel}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {domains.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            No websites connected yet
          </div>
        ) : (
          domains.map((domain) => (
            <DropdownMenuItem
              key={domain.id}
              onSelect={() => handleSelect(domain.id)}
              className={cn(
                "cursor-pointer",
                domain.id === currentDomain?.id && "bg-muted font-medium",
              )}
            >
              <Globe className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{extractDomain(domain.url)}</span>
            </DropdownMenuItem>
          ))
        )}
        {/* Adding a domain is an admin action -- the setup wizard it opens
            writes a domain doc that firestore.rules only lets an admin create.
            Hide it for viewers rather than offer a dead-end affordance. */}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={handleAddNewWebsite}
              className="cursor-pointer"
            >
              <Plus className="mr-2 h-4 w-4 shrink-0" />
              Add new website
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
